import React from 'react';

import { Button, LoadingButton, Modal } from '@common/index';
import { Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, State, flowActions } from '@store/index';

import { SecurityOutlined } from '@mui/icons-material';
import { useTheme } from '../../styles/theme';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { assignWorkspaceAdmin } from '@requests/users';
import { listWorkspaceUsers } from '@requests/roles';
import { getServices } from '@store/async/service';

export const AssignWorkspaceAdmin: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const userAssignedId = useSelector((state: State) => state.flow.assignWorkspaceAdmin);
  const modalOpen = Boolean(userAssignedId);

  const { data: users } = useQuery({
    queryKey: ['roles', { serviceId: service_id }],
    queryFn: listWorkspaceUsers,
    staleTime: 30000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    enabled: Boolean(service_id),
  });

  const user = users?.find((user) => user.id === userAssignedId);

  const { mutateAsync: assign, isPending } = useMutation({
    mutationFn: assignWorkspaceAdmin,
    onSuccess: (data) => {
      if (data?.error) {
        enqueueSnackbar('Failed to assign admin permissions!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar(data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      dispatch(getServices());
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      dispatch(flowActions.setAssignWorkspaceAdmin(null));
    },
  });

  const giveAdmin = async () => {
    const serviceId = service_id as string;
    const userId = userAssignedId as string;

    await assign({ serviceId, userId });
  };

  return (
    <Modal
      open={modalOpen}
      setOpen={(val) => dispatch(flowActions.setAssignWorkspaceAdmin(val))}
      header='Grant Admin Privileges'
      background={theme.primary}
      identifier={user?.email}
      icon={<SecurityOutlined />}
      info={`You are about to grant admin privileges to ${user?.email}. This will allow the user to delete the workspace and assign/create/remove permission groups for other users. The organization owner always has admin permissions for all workspaces, so this only applies to sub-users. If you would like to proceed, please confirm below.`}
      actions={
        <Stack direction='row' spacing={1}>
          <Button small onClick={() => dispatch(flowActions.setAssignWorkspaceAdmin(null))}>
            Cancel
          </Button>
          <LoadingButton loading={isPending} minWidth='70px' small customColor='primary' onClick={giveAdmin}>
            Assign
          </LoadingButton>
        </Stack>
      }
    />
  );
};
